import React, { useState, useEffect } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { WorkStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import { useWork } from '../../context/WorkContext';
import { INDIAN_STATES, getDistrictsForState } from '../../data/indianDistricts';
import ThemedText from '../../components/common/ThemedText';
import { CustomPicker } from '../../components/common/CustomPicker';
import useKeyboardHeight from '../../hooks/useKeyboardHeight';
import FormFooterSpacer from '../../components/common/FormFooterSpacer';
import { showSuccessToast, showErrorToast } from '../../utils/toast';

const SUPPLY_CATEGORIES = [
  'Construction Materials',
  'Steel & Metals',
  'Cement & Concrete',
  'Wood & Timber',
  'Sand & Aggregates',
  'Bricks & Blocks',
  'Electrical Materials',
  'Plumbing Materials',
  'Paint & Coatings',
  'Hardware & Tools',
  'Chemicals & Adhesives',
  'Plastics & Polymers',
  'Glass & Ceramics',
  'Textiles & Fabrics',
  'Packaging Materials',
  'Office Supplies',
  'Food & Beverages',
  'Agricultural Supplies',
  'Industrial Equipment',
  'Machinery Parts',
  'Electronics Components',
  'Safety Equipment',
  'Cleaning Supplies',
  'Other Raw Materials',
];

const SUPPLY_UNITS = [
  'Tons', 'Kg', 'Grams', 'Pounds',
  'Liters', 'Gallons', 'Cubic Meters',
  'Meters', 'Feet', 'Yards',
  'Pieces', 'Units', 'Boxes', 'Dozens', 'Sets',
  'Sq. Meters', 'Sq. Feet',
];

type AddSupplyNavigationProp = StackNavigationProp<WorkStackParamList, 'AddSupply'>;

const AddSupplyScreen: React.FC = () => {
  const navigation = useNavigation<AddSupplyNavigationProp>();
  const { theme } = useTheme();
  const { createJob } = useWork();
  const insets = useSafeAreaInsets();
  
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [budget, setBudget] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  
  const [errors, setErrors] = useState({ title: false, category: false, description: false, quantity: false, unit: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(60);
  const { keyboardHeight } = useKeyboardHeight();

  // Get districts based on selected state
  const availableDistricts = state ? getDistrictsForState(state) : [];

  const handlePostSupply = async () => {
    setErrors({ title: false, category: false, description: false, quantity: false, unit: false });
    const finalUnit = unit === 'Custom' ? customUnit : unit;
    const newErrors = {
      title: !title.trim(),
      category: !category.trim(),
      description: !description.trim(),
      quantity: !quantity.trim() || isNaN(parseFloat(quantity)),
      unit: !finalUnit.trim(),
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(error => error)) return;

    setIsSubmitting(true);
    try {
      await createJob({
        type: 'supply' as any,
        title: title.trim(),
        category,
        description: description.trim(),
        budget: budget ? parseFloat(budget.replace(/[^0-9.]/g, '')) : undefined,
        quantity: parseFloat(quantity),
        unit: finalUnit.trim(),
        state: state || undefined,
        district: district || undefined,
      } as any);
      showSuccessToast('Supply request posted successfully!');
      navigation.goBack();
    } catch (error) {
      showErrorToast('Failed to post supply request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top']}>
      <KeyboardAvoidingView 
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View 
          style={[styles.header, { borderBottomColor: theme.border }]}
          onLayout={(event) => {
            const { height } = event.nativeEvent.layout;
            setHeaderHeight(height);
          }}
        >
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.cancelButton}>
            <ThemedText style={[styles.cancelText, { color: theme.textSecondary }]}>Cancel</ThemedText>
          </TouchableOpacity>
          <ThemedText style={styles.headerTitle}>Post Supply Request</ThemedText>
          <View style={styles.placeholder} />
        </View>

        <ScrollView 
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Supply Title <ThemedText style={styles.required}>*</ThemedText>
            </ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.card, borderColor: errors.title ? theme.error : theme.border, color: theme.text }]}
              placeholder="e.g., Iron Rods, Cement Bags, etc."
              placeholderTextColor={theme.textTertiary}
              value={title}
              onChangeText={setTitle}
            />
          </View>

          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Category <ThemedText style={styles.required}>*</ThemedText>
            </ThemedText>
            <CustomPicker
              selectedValue={category || undefined}
              onValueChange={(value) => setCategory(value || '')}
              items={[
                { label: 'Select Category', value: undefined },
                ...SUPPLY_CATEGORIES.map(cat => ({ label: cat, value: cat }))
              ]}
              placeholder="Select Category"
              searchable={true}
              searchPlaceholder="Search categories..."
            />
          </View>

          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Quantity <ThemedText style={styles.required}>*</ThemedText>
            </ThemedText>
            <TextInput
              style={[styles.input, { backgroundColor: theme.card, borderColor: errors.quantity ? theme.error : theme.border, color: theme.text }]}
              placeholder="e.g., 10, 100, 500"
              placeholderTextColor={theme.textTertiary}
              value={quantity}
              onChangeText={setQuantity}
              keyboardType="decimal-pad"
            />
          </View>

          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Unit <ThemedText style={styles.required}>*</ThemedText>
            </ThemedText>
            <CustomPicker
              selectedValue={unit || undefined}
              onValueChange={(value) => setUnit(value || '')}
              items={[
                { label: 'Select Unit', value: undefined },
                ...SUPPLY_UNITS.map(u => ({ label: u, value: u })),
                { label: 'Custom', value: 'Custom' }
              ]}
              placeholder="Select Unit"
              searchable={true}
              searchPlaceholder="Search units..."
            />
            {unit === 'Custom' && (
              <TextInput
                style={[styles.input, styles.customUnitInput, { backgroundColor: theme.card, borderColor: theme.border, color: theme.text }]}
                placeholder="Enter custom unit"
                placeholderTextColor={theme.textTertiary}
                value={customUnit}
                onChangeText={setCustomUnit}
              />
            )}
          </View>

          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>Budget (Optional)</ThemedText>
            <View style={[styles.budgetInput, { backgroundColor: theme.card, borderColor: theme.border }]}>
              <ThemedText style={[styles.currencySymbol, { color: theme.textSecondary }]}>$</ThemedText>
              <TextInput
                style={[styles.budgetValue, { color: theme.text }]}
                placeholder="0"
                placeholderTextColor={theme.textTertiary}
                value={budget}
                onChangeText={setBudget}
                keyboardType="decimal-pad"
              />
            </View>
          </View>

          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>State (Optional)</ThemedText>
            <CustomPicker
              selectedValue={state || undefined}
              onValueChange={(value) => {
                setState(value || '');
                // Reset district when state changes
                if (value !== state) {
                  setDistrict('');
                }
              }}
              items={[
                { label: 'Select State', value: undefined },
                ...INDIAN_STATES.map(s => ({ label: s, value: s }))
              ]}
              placeholder="Select State"
              searchable={true}
              searchPlaceholder="Search states..."
            />
          </View>

          {state && (
            <View style={styles.fieldContainer}>
              <ThemedText style={[styles.label, { color: theme.text }]}>District (Optional)</ThemedText>
              <CustomPicker
                selectedValue={district || undefined}
                onValueChange={(value) => setDistrict(value || '')}
                items={[
                  { label: 'Select District', value: undefined },
                  ...availableDistricts.map(d => ({ label: d, value: d }))
                ]}
                placeholder="Select District"
                searchable={true}
                searchPlaceholder="Search districts..."
              />
            </View>
          )}

          <View style={styles.fieldContainer}>
            <ThemedText style={[styles.label, { color: theme.text }]}>
              Description <ThemedText style={styles.required}>*</ThemedText>
            </ThemedText>
            <TextInput
              style={[styles.textArea, { backgroundColor: theme.card, borderColor: errors.description ? theme.error : theme.border, color: theme.text }]}
              placeholder="Describe the supply requirements, quality standards, delivery expectations, etc."
              placeholderTextColor={theme.textTertiary}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={6}
              textAlignVertical="top"
            />
          </View>

          {/* Action Button */}
          <TouchableOpacity
            style={[styles.postButton, { backgroundColor: theme.accent }]}
            onPress={handlePostSupply}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <ThemedText style={styles.postButtonText}>Post Supply Request</ThemedText>
            )}
          </TouchableOpacity>

          <FormFooterSpacer
            headerHeight={headerHeight}
            bottomInset={insets.bottom}
            keyboardHeight={keyboardHeight}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  keyboardView: { flex: 1 },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  cancelButton: { paddingVertical: 8, paddingRight: 16 },
  cancelText: { fontSize: 16 },
  headerTitle: { fontSize: 18, fontWeight: '600' },
  placeholder: { width: 60 },
  content: { flex: 1 },
  scrollContent: { padding: 20, paddingBottom: 24 },
  fieldContainer: { marginBottom: 24 },
  label: { fontSize: 16, fontWeight: '600', marginBottom: 8 },
  required: { color: '#EF4444' },
  input: { borderWidth: 1, borderRadius: 12, padding: 16, fontSize: 16 },
  customUnitInput: { marginTop: 12 },
  pickerButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 1, borderRadius: 12, padding: 16 },
  pickerText: { fontSize: 16 },
  budgetInput: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 12, paddingHorizontal: 16 },
  currencySymbol: { fontSize: 18, fontWeight: '600', marginRight: 8 },
  budgetValue: { flex: 1, fontSize: 16, paddingVertical: 16 },
  textArea: { borderWidth: 1, borderRadius: 12, padding: 16, fontSize: 16, minHeight: 120 },
  postButton: { borderRadius: 12, paddingVertical: 16, alignItems: 'center', marginTop: 8 },
  postButtonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '700' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0, 0, 0, 0.5)', justifyContent: 'flex-end' },
  modalContent: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '70%' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 20, borderBottomWidth: 1 },
  modalTitle: { fontSize: 18, fontWeight: '700' },
  searchInput: { margin: 16, marginBottom: 8, borderWidth: 1, borderRadius: 12, padding: 12, fontSize: 16 },
  modalItem: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1 },
  modalItemText: { fontSize: 16 },
});

export default AddSupplyScreen;
